import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    CircularProgress,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    Typography,
    Paper,
    Fade,
    IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Search } from "@domain/insights/models/Search";
import { useSearchTerms } from "../../hooks/useSearchTerms";
import { Close } from "@mui/icons-material";
import ForecastGrade from "../ForecastAnalytics/ForecastGrade";
import { useIntersectionObserver } from "@shared/hooks/useIntersectionObserver";

/**
 * A spotlight-style searchable modal/component to search and select search terms.
 * Props:
 *   open: boolean (if the spotlight is displayed)
 *   onClose: function (triggered when spotlight is dismissed)
 *   onSelect: function (called with the chosen searchTerm string)
 */
interface SearchTermSpotlightProps {
    open: boolean;
    onClose: () => void;
    onSelect: (searchTerm: string) => void;
}

const SearchTermSpotlight: React.FC<SearchTermSpotlightProps> = ({
    open,
    onClose,
    onSelect,
}) => {
    const [search, setSearch] = useState("");
    const {
        searchTerms,
        isLoading: loading,
        isFetchingNextPage,
        hasNextPage,
        fetchNextPage,
    } = useSearchTerms({
        searchTerm: search || undefined,
    });

    const listRef = useRef<HTMLUListElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const { isIntersecting } = useIntersectionObserver({
        elementRef: sentinelRef,
        root: listRef.current,
        rootMargin: '100px',
    });

    useEffect(() => {
        if (isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Handle keyboard navigation and closing
    React.useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [open, onClose]);

    // Autofocus input when open
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    React.useEffect(() => {
        if (open && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open]);

    return (
        <Fade in={open}>
            <Box
                sx={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 1300,
                    background: "rgba(0,0,0,0.2)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                }}
                onClick={onClose}
            >
                <Paper
                    elevation={8}
                    sx={{
                        mt: 12,
                        minWidth: { xs: '90vw', sm: 400, md: 500 },
                        maxWidth: 600,
                        maxHeight: '60vh',
                        borderRadius: 2,
                        p: 2,
                        outline: "none",
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    <Stack spacing={2} sx={{ minHeight: 0, flex: 1, overflow: 'hidden' }}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                            <TextField
                                placeholder="Search search terms"
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={search}
                                inputRef={inputRef}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") onClose();
                                }}
                            />
                            <IconButton onClick={() => onClose()}> <Close /></IconButton>

                        </Stack>

                        {loading && (
                            <Box display="flex" justifyContent="center" py={3}>
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {!loading && (
                            <List ref={listRef} dense sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                                {searchTerms.length === 0 && (
                                    <Typography variant="body2" color="text.secondary" align="center" sx={{ p: 2 }}>
                                        No search terms found
                                    </Typography>
                                )}
                                {searchTerms.map((item: Search) => (
                                    <ListItemButton
                                        key={`${item.searchTerm}-${item.connectorType}`}
                                        onClick={() => { onSelect(item.searchTerm); onClose(); }}
                                    >
                                        <ListItemText
                                            primary={item.searchTerm}
                                            secondary={item.connectorType}
                                        />
                                        <ForecastGrade size="small" grade={item.forecastGrade} />
                                    </ListItemButton>
                                ))}
                                <div ref={sentinelRef} />
                                {isFetchingNextPage && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                )}
                            </List>
                        )}
                    </Stack>
                </Paper>
            </Box>
        </Fade>
    );
};

export default SearchTermSpotlight;
